import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ModuleSection } from '@platform/entitlements';
import './StartPage.css';

/**
 * AI Readiness — the module's Start Menu.
 *
 * CONTENT is the source's, from `ai_readiness_template/views/start-menu.ejs`: the
 * greeting, "From raw idea to running delivery.", the lede, the two-path fork, the five
 * journey steps with their titles and descriptions, and the express-lane pair.
 *
 * DESIGN is APR's (owner's ruling, 2026-08-04) — the same hero, fork, block labels, tiles
 * and support strip, so the two products' start screens read as one platform rather than
 * two apps that happen to be behind the same login. `StartPage.css` here is APR's file
 * re-scoped to `.air-start`; the note at its head explains why it is a copy and what that
 * costs.
 *
 * Tools whose module is not ported render but do NOT navigate. The source's journey links
 * across the whole product and only Strategic Intelligence exists here; a link to a screen
 * that is not there would be a dead end, and dropping them would misrepresent the method.
 * Each becomes live by adding a `path` below and nothing else.
 */

/** Where "Contact your AI Readiness advisor" goes — the same support inbox as APR. */
const ADVISOR_MAILTO =
  'mailto:support@talogent.com' +
  '?subject=' +
  encodeURIComponent('AI Readiness — request for assistance') +
  '&body=' +
  encodeURIComponent(
    'Tell us what you need help with and we will get back to you.\n\n' +
      '(Sent from AI Readiness)\n',
  );

interface Tool {
  title: string;
  description: string;
  /** The platform route, when the screen exists. Absent means not ported yet. */
  path?: string;
}

interface Step {
  n: number;
  title: string;
  /** A quiet qualifier after the title, as the source has on step 5. */
  qualifier?: string;
  description: string;
  tools: Tool[];
}

/** The five steps, copy verbatim from the source. */
const STEPS: readonly Step[] = [
  {
    n: 1,
    title: 'Score — objectively',
    description:
      "Capture every initiative and score it against one consistent framework, so priority reflects evidence, not the loudest voice in the room. You get an unbiased ranking of what's genuinely worth doing.",
    tools: [
      { title: 'Opportunities', description: 'Capture & score initiatives' },
      { title: 'Scoring Framework', description: 'Dimensions, criteria & weights' },
      {
        title: 'Strategic Intelligence',
        description: 'Discovery & unbiased analysis',
        path: '/ai_readiness/strategic-intelligence',
      },
    ],
  },
  {
    n: 2,
    title: 'Weigh the benefit',
    description:
      'Put a number on the business value each opportunity delivers — savings, ROI, and the value it realizes over time — so a go/no-go is defensible rather than a gut call.',
    tools: [
      { title: 'Benefits & ROI', description: 'Value & ROI rollups' },
      { title: 'Portfolio Optimizer', description: 'Value scenarios & trade-offs' },
    ],
  },
  {
    n: 3,
    title: 'Weigh the cost',
    description:
      'Model what each initiative costs to build and to run — MVP cost, discounted cash flow, and the resource economics per phase — then set benefit against cost to decide the disposition.',
    tools: [
      { title: 'MVP Cost', description: 'Build-cost scenarios' },
      { title: 'NPV Model', description: 'Discounted cash flow' },
      { title: 'Delivery Economics', description: 'Cost per phase & mix' },
      { title: 'Disposition', description: 'Benefit vs. cost, decided' },
    ],
  },
  {
    n: 4,
    title: 'Shape the roadmap',
    description:
      "Sequence the work you've chosen into a high-level implementation roadmap — phases, dependencies and timelines — and see the whole portfolio's shape before you commit a single team.",
    tools: [
      { title: 'Roadmap Planning', description: 'Sequence phases & dependencies' },
      { title: 'Planning Dashboard', description: 'Score, cost & value at a glance' },
    ],
  },
  {
    n: 5,
    title: 'Run delivery',
    qualifier: 'Agile or Waterfall',
    description:
      'Take the roadmap into delivery and run it your way — define the programs, lock the timeline, then track progress, budget and effort against plan, all the way to sign-off.',
    tools: [
      { title: 'Roadmap Definition', description: 'Programs, opportunities & phases' },
      { title: 'Timeline Finalization', description: 'Lock phase dates' },
      { title: 'Execution Control Center', description: 'Progress & delivery status' },
      { title: 'Execution Dashboard', description: 'Budget, effort & delivery health' },
    ],
  },
];

/** The express lane, copy verbatim from the source. */
const EXPRESS: ReadonlyArray<{ n: string; t: string; d: string }> = [
  {
    n: 'Step 1',
    t: 'Import your roadmap',
    d: 'Upload a Waterfall or Agile template on Roadmap Definition — the delivery program, phases and timeline are created in one shot.',
  },
  {
    n: 'Step 2',
    t: 'Run delivery',
    d: 'Track progress, budget and effort in the Execution Control Center, and report status — Agile or Waterfall.',
  },
];

function Arrow() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
      <path d="M5 12h13M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export interface AiReadinessStartPageProps {
  firstName?: string;
  sections: readonly ModuleSection[];
}

export function AiReadinessStartPage({ firstName }: AiReadinessStartPageProps) {
  const navigate = useNavigate();
  const [view, setView] = useState<'welcome' | 'journey' | 'express'>('welcome');
  const [open, setOpen] = useState<number | null>(1);

  const liveTools = STEPS.flatMap((s) => s.tools).filter((t) => t.path);
  const anyInert = STEPS.some((s) => s.tools.some((t) => !t.path));

  if (view === 'welcome') {
    return (
      <div className="air-start">
        <span className="hello">Welcome, {firstName || 'there'}</span>
        <h1>
          From raw idea to <span style={{ fontWeight: 300 }}>running delivery.</span>
        </h1>
        <p className="sub">
          Pick how you want to work. Everything else follows one honest line — score what
          matters, weigh its value and cost, shape a roadmap, then deliver it. No wall of
          buttons to hunt through.
        </p>

        <div className="paths">
          <button className="path primary" onClick={() => setView('journey')}>
            <span className="mode">The full journey · recommended</span>
            <h3>Start from scratch</h3>
            <span className="lead">
              You have opportunities, but no plan yet. Let the platform score your initiatives
              objectively, quantify the benefit and the cost, shape a high-level roadmap, then
              take it into delivery.
            </span>
            <span className="go">
              Follow the five steps <Arrow />
            </span>
          </button>
          <button className="path" onClick={() => setView('express')}>
            <span className="mode">The express lane</span>
            <h3>Bring your own plan</h3>
            <span className="lead">
              You already have a finalized roadmap. Skip the analysis. Import your Waterfall or
              Agile plan and go straight to execution — define delivery, track it, report it.
            </span>
            <span className="go">
              Import &amp; execute <Arrow />
            </span>
          </button>
        </div>

        <div className="block-label">
          <h2>Jump straight to</h2>
          <span className="rule"></span>
        </div>
        <div className="quick">
          {liveTools.map((t) => (
            <button key={t.title} className="tile" onClick={() => navigate(t.path!)}>
              <span>
                <b>{t.title}</b>
                <span>{t.description}</span>
              </span>
            </button>
          ))}
        </div>

        <div className="support">
          <p>
            <b>Need Assistance?</b> Our team runs working sessions with you — the platform guides
            you, and you&apos;re never on your own.
          </p>
          <a className="btn btn-ghost cta" href={ADVISOR_MAILTO}>
            Contact your AI Readiness advisor
          </a>
        </div>
      </div>
    );
  }

  if (view === 'express') {
    return (
      <div className="air-start">
        <button className="backlink" onClick={() => setView('welcome')}>
          ← Back to welcome screen
        </button>
        <span className="hello">The express lane</span>
        <h1>You already have the plan — let&apos;s execute it.</h1>
        <div className="steps">
          {EXPRESS.map((s) => (
            <div key={s.n} className="stepcard">
              <span className="mode">{s.n}</span>
              <h3>{s.t}</h3>
              <span className="lead">{s.d}</span>
            </div>
          ))}
        </div>
        {anyInert ? <p className="inertnote">{INERT_NOTE}</p> : null}
      </div>
    );
  }

  return (
    <div className="air-start">
      <button className="backlink" onClick={() => setView('welcome')}>
        ← Back to welcome screen
      </button>
      <span className="hello">Your guided journey</span>
      <h1>
        Welcome, {firstName || 'there'} — here&apos;s how AI Readiness works, step by step
      </h1>
      <p className="sub">
        Not sure? <b>Start from scratch</b> is the complete method. You can switch an opportunity
        to the express lane any time before it enters delivery.
      </p>

      <div className="block-label">
        <h2>The full journey</h2>
        <span className="rule"></span>
        <span className="count">5 steps</span>
      </div>

      <div className="steps">
        {STEPS.map((step) => {
          const isOpen = open === step.n;
          return (
            <div key={step.n} className={'stepcard' + (isOpen ? ' open' : '')}>
              <button className="stephead" onClick={() => setOpen(isOpen ? null : step.n)}>
                <span className="num">{step.n}</span>
                <span className="stepmain">
                  <h3>
                    {step.title}
                    {step.qualifier ? <span className="qual"> {step.qualifier}</span> : null}
                  </h3>
                  <span className="lead">{step.description}</span>
                </span>
                <span className={'caret' + (isOpen ? ' on' : '')}>›</span>
              </button>
              {isOpen ? (
                <div className="stepbody">
                  <span className="mode">What&apos;s in here</span>
                  <div className="quick">
                    {step.tools.map((t) => (
                      <button
                        key={t.title}
                        className={'tile' + (t.path ? '' : ' inert')}
                        onClick={t.path ? () => navigate(t.path!) : undefined}
                        disabled={!t.path}
                      >
                        <span>
                          <b>{t.title}</b>
                          <span>{t.description}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {anyInert ? <p className="inertnote">{INERT_NOTE}</p> : null}
    </div>
  );
}

const INERT_NOTE =
  'Tools shown without a link belong to modules that are not migrated yet. They appear here ' +
  "because this is the product's own journey; each becomes live as its module lands.";
